import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Heart,
  Award,
  Trophy,
  Send,
  Star,
  TrendingUp,
  Plus,
  RefreshCw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { apiClient } from "../services/api";
import { toast } from "sonner";

const TABS = [
  { id: "feed",        icon: Heart,    section: "community" },
  { id: "kudos",       icon: Send,     section: "community" },
  { id: "badges",      icon: Award,    section: "badges" },
  { id: "leaderboard", icon: Trophy,   section: "stats" },
];

export default function RecognitionHub() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "feed";

  const setTab = (tab) => setSearchParams({ tab });

  const sections = [...new Set(TABS.map((t) => t.section))];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Heart className="h-6 w-6 text-red-500" />
            {t("recognitionHub.title")}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            {t("recognitionHub.subtitle")}
          </p>
        </div>
      </div>

      {/* Tab nav */}
      <div className="border-b">
        {sections.map((section) => (
          <div key={section} className="inline-block mr-6 mb-0">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-3">
              {t(`recognitionHub.sections.${section}`)}
            </span>
            {TABS.filter((t) => t.section === section).map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setTab(tab.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors mr-1 ${
                    activeTab === tab.id
                      ? "border-primary text-primary"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {t(`recognitionHub.tabs.${tab.id}`)}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Content */}
      <div>
        {activeTab === "feed"        && <FeedTab />}
        {activeTab === "kudos"       && <KudosTab />}
        {activeTab === "badges"      && <BadgesTab />}
        {activeTab === "leaderboard" && <LeaderboardTab />}
      </div>
    </div>
  );
}

// ── Feed ─────────────────────────────────────────────────────────────────────

function FeedTab() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("v1/recognition/feed?limit=30");
      setPosts(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error(t("recognitionHub.feed.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const react = async (postId, emoji) => {
    try {
      await apiClient.post(`v1/recognition/feed/${postId}/reactions`, { emoji });
      load();
    } catch {
      toast.error(t("recognitionHub.feed.reactionError"));
    }
  };

  if (loading) return <p className="text-muted-foreground">{t("recognitionHub.loading")}</p>;

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t("recognitionHub.feed.count", { count: total })}</p>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-3 w-3 mr-1" /> {t("recognitionHub.feed.refresh")}
        </Button>
      </div>

      {posts.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Heart className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>{t("recognitionHub.feed.empty")}</p>
            <p className="text-sm">{t("recognitionHub.feed.emptyHint")}</p>
          </CardContent>
        </Card>
      )}

      {posts.map((post) => (
        <PostCard key={post.id} post={post} onReact={react} />
      ))}
    </div>
  );
}

function PostCard({ post, onReact }) {
  const { t } = useTranslation();
  const sender = post.sender;
  const recipient = post.recipient;

  const reactionCounts = {};
  (post.reactions || []).forEach((r) => {
    reactionCounts[r.emoji] = (reactionCounts[r.emoji] || 0) + 1;
  });

  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-pink-400 to-red-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
            {sender ? sender.first_name?.[0] : "?"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm">
              <span className="font-semibold">
                {sender ? `${sender.first_name} ${sender.last_name}` : t("recognitionHub.feed.someone")}
              </span>{" "}
              {t("recognitionHub.feed.recognized")}{" "}
              <span className="font-semibold text-primary">
                {recipient ? `${recipient.first_name} ${recipient.last_name}` : t("recognitionHub.feed.aColleague")}
              </span>
              {post.badge && (
                <span className="ml-1">
                  {post.badge.emoji}{" "}
                  <Badge variant="secondary" className="text-xs">
                    {post.badge.name}
                  </Badge>
                </span>
              )}
            </p>

            <p className="mt-2 text-sm text-foreground leading-relaxed">
              "{post.message}"
            </p>

            {post.values_tags?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {post.values_tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">
                    #{tag}
                  </Badge>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 mt-3">
              {["👍", "❤️", "🎉", "🌟"].map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => onReact(post.id, emoji)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors bg-muted hover:bg-muted/80 rounded-full px-2 py-0.5"
                >
                  {emoji}
                  {reactionCounts[emoji] ? (
                    <span className="font-medium">{reactionCounts[emoji]}</span>
                  ) : null}
                </button>
              ))}
              <span className="ml-auto text-xs text-muted-foreground">
                {new Date(post.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Kudos form ────────────────────────────────────────────────────────────────

function KudosTab() {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [badges, setBadges] = useState([]);
  const [form, setForm] = useState({
    recipient_id: "",
    message: "",
    badge_id: "none",
    is_public: true,
    values_tags: [],
  });
  const [tagInput, setTagInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("v1/employees/?limit=200").then((r) => setEmployees(r.data.employees || r.data || []));
    apiClient.get("v1/recognition/badges").then((r) => setBadges(r.data || []));
  }, []);

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/\s+/g, "-");
    if (t && !form.values_tags.includes(t)) {
      setForm((f) => ({ ...f, values_tags: [...f.values_tags, t] }));
    }
    setTagInput("");
  };

  const removeTag = (tag) =>
    setForm((f) => ({ ...f, values_tags: f.values_tags.filter((x) => x !== tag) }));

  const submit = async () => {
    if (!form.recipient_id || !form.message.trim()) {
      toast.error(t("recognitionHub.kudos.requiredFields"));
      return;
    }
    setSaving(true);
    try {
      await apiClient.post("v1/recognition/feed", {
        ...form,
        recipient_id: parseInt(form.recipient_id),
        badge_id: form.badge_id && form.badge_id !== "none" ? parseInt(form.badge_id) : null,
      });
      toast.success(t("recognitionHub.kudos.sent"));
      setForm({ recipient_id: "", message: "", badge_id: "none", is_public: true, values_tags: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || t("recognitionHub.kudos.sendError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Send className="h-4 w-4" /> {t("recognitionHub.kudos.title")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-1 block">{t("recognitionHub.kudos.fieldRecipient")}</label>
            <Select value={form.recipient_id} onValueChange={(v) => setForm((f) => ({ ...f, recipient_id: v }))}>
              <SelectTrigger>
                <SelectValue placeholder={t("recognitionHub.kudos.recipientPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {employees.map((e) => (
                  <SelectItem key={e.id} value={String(e.id)}>
                    {e.first_name} {e.last_name}
                    {e.department?.name ? ` — ${e.department.name}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">{t("recognitionHub.kudos.fieldBadge")}</label>
            <Select value={form.badge_id} onValueChange={(v) => setForm((f) => ({ ...f, badge_id: v }))}>
              <SelectTrigger>
                <SelectValue placeholder={t("recognitionHub.kudos.noBadge")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("recognitionHub.kudos.noBadge")}</SelectItem>
                {badges.map((b) => (
                  <SelectItem key={b.id} value={String(b.id)}>
                    {b.emoji} {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">{t("recognitionHub.kudos.fieldMessage")}</label>
            <Textarea
              placeholder={t("recognitionHub.kudos.messagePlaceholder")}
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              rows={4}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground text-right mt-0.5">
              {form.message.length}/500
            </p>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">{t("recognitionHub.kudos.fieldValues")}</label>
            <div className="flex gap-2">
              <Input
                placeholder={t("recognitionHub.kudos.valuesPlaceholder")}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
              />
              <Button type="button" variant="outline" size="sm" onClick={addTag}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {form.values_tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {form.values_tags.map((tag) => (
                  <Badge
                    key={tag}
                    variant="secondary"
                    className="cursor-pointer"
                    onClick={() => removeTag(tag)}
                  >
                    #{tag} ×
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <Button onClick={submit} disabled={saving} className="w-full">
            {saving ? t("recognitionHub.kudos.sending") : t("recognitionHub.kudos.sendCta")}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Badges ────────────────────────────────────────────────────────────────────

function BadgesTab() {
  const { t } = useTranslation();
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", emoji: "🏆", color: "gold", category: "general" });
  const [saving, setSaving] = useState(false);

  const COLORS = ["gold", "silver", "bronze", "blue", "green", "purple"];
  const CATEGORIES = ["general", "teamwork", "innovation", "leadership", "customer", "excellence"];

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("v1/recognition/badges?active_only=false");
      setBadges(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createBadge = async () => {
    if (!form.name.trim()) { toast.error(t("recognitionHub.badges.nameRequired")); return; }
    setSaving(true);
    try {
      await apiClient.post("v1/recognition/badges", form);
      toast.success(t("recognitionHub.badges.created"));
      setShowForm(false);
      setForm({ name: "", description: "", emoji: "🏆", color: "gold", category: "general" });
      load();
    } catch {
      toast.error(t("recognitionHub.badges.createError"));
    } finally {
      setSaving(false);
    }
  };

  const colorBadge = (color) => {
    const map = { gold: "bg-yellow-100 text-yellow-800", silver: "bg-gray-100 text-gray-700", bronze: "bg-orange-100 text-orange-800", blue: "bg-blue-100 text-blue-800", green: "bg-green-100 text-green-800", purple: "bg-purple-100 text-purple-800" };
    return map[color] || "bg-gray-100 text-gray-700";
  };

  if (loading) return <p className="text-muted-foreground">{t("recognitionHub.loading")}</p>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{t("recognitionHub.badges.count", { count: badges.length })}</p>
        <Button size="sm" onClick={() => setShowForm(!showForm)}>
          <Plus className="h-4 w-4 mr-1" /> {t("recognitionHub.badges.new")}
        </Button>
      </div>

      {showForm && (
        <Card className="border-primary/30">
          <CardHeader><CardTitle className="text-sm">{t("recognitionHub.badges.createTitle")}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium mb-1 block">{t("recognitionHub.badges.fieldEmoji")}</label>
                <Input value={form.emoji} onChange={(e) => setForm((f) => ({ ...f, emoji: e.target.value }))} className="w-20" />
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">{t("recognitionHub.badges.fieldName")}</label>
                <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder={t("recognitionHub.badges.namePlaceholder")} />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">{t("recognitionHub.badges.fieldDescription")}</label>
              <Input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder={t("recognitionHub.badges.optional")} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium mb-1 block">{t("recognitionHub.badges.fieldColor")}</label>
                <Select value={form.color} onValueChange={(v) => setForm((f) => ({ ...f, color: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {COLORS.map((c) => <SelectItem key={c} value={c}>{t(`recognitionHub.badges.colors.${c}`, { defaultValue: c })}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium mb-1 block">{t("recognitionHub.badges.fieldCategory")}</label>
                <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{t(`recognitionHub.badges.categories.${c}`, { defaultValue: c })}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={createBadge} disabled={saving}>{saving ? t("recognitionHub.badges.creating") : t("recognitionHub.badges.createCta")}</Button>
              <Button size="sm" variant="outline" onClick={() => setShowForm(false)}>{t("recognitionHub.common.cancel")}</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {badges.map((b) => (
          <Card key={b.id} className={!b.is_active ? "opacity-50" : ""}>
            <CardContent className="pt-4 pb-3 text-center">
              <div className="text-3xl mb-1">{b.emoji}</div>
              <p className="font-semibold text-sm">{b.name}</p>
              {b.description && <p className="text-xs text-muted-foreground mt-0.5">{b.description}</p>}
              <div className="flex justify-center gap-1 mt-2">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colorBadge(b.color)}`}>
                  {t(`recognitionHub.badges.colors.${b.color}`, { defaultValue: b.color })}
                </span>
                <Badge variant="outline" className="text-xs">{t(`recognitionHub.badges.categories.${b.category}`, { defaultValue: b.category })}</Badge>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ── Leaderboard ───────────────────────────────────────────────────────────────

function LeaderboardTab() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [period, setPeriod] = useState("month");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (p) => {
    setLoading(true);
    try {
      const [lbRes, statsRes] = await Promise.all([
        apiClient.get(`v1/recognition/leaderboard?period=${p}&limit=10`),
        apiClient.get("v1/recognition/stats"),
      ]);
      setData(lbRes.data || []);
      setStats(statsRes.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(period); }, [period]);

  const medalColor = (rank) => {
    if (rank === 1) return "text-yellow-500";
    if (rank === 2) return "text-gray-400";
    if (rank === 3) return "text-orange-500";
    return "text-muted-foreground";
  };

  const PERIODS = [
    { value: "week" },
    { value: "month" },
    { value: "quarter" },
    { value: "year" },
  ];

  return (
    <div className="space-y-6 max-w-2xl">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={Heart} label={t("recognitionHub.leaderboard.statRecognitions")} value={stats.total_posts} color="text-red-500" />
          <StatCard icon={TrendingUp} label={t("recognitionHub.leaderboard.statThisMonth")} value={stats.posts_this_month} color="text-blue-500" />
          <StatCard icon={Award} label={t("recognitionHub.leaderboard.statBadgesAwarded")} value={stats.total_badges_awarded} color="text-yellow-500" />
          <StatCard icon={Star} label={t("recognitionHub.leaderboard.statActiveBadges")} value={stats.active_badges} color="text-purple-500" />
        </div>
      )}

      <div className="flex items-center gap-3">
        <Trophy className="h-5 w-5 text-yellow-500" />
        <h2 className="font-semibold">{t("recognitionHub.leaderboard.title")}</h2>
        <Select value={period} onValueChange={(v) => { setPeriod(v); load(v); }}>
          <SelectTrigger className="w-40 ml-auto">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIODS.map((p) => (
              <SelectItem key={p.value} value={p.value}>{t(`recognitionHub.leaderboard.periods.${p.value}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-muted-foreground">{t("recognitionHub.loading")}</p>
      ) : data.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <Trophy className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p>{t("recognitionHub.leaderboard.empty")}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {data.map((entry) => (
            <Card key={entry.employee_id} className={entry.rank <= 3 ? "border-primary/20 bg-primary/5" : ""}>
              <CardContent className="py-3 px-4 flex items-center gap-4">
                <div className={`text-2xl font-bold w-8 text-center ${medalColor(entry.rank)}`}>
                  {entry.rank <= 3 ? ["🥇", "🥈", "🥉"][entry.rank - 1] : `#${entry.rank}`}
                </div>
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                  {entry.employee_name?.[0] || "?"}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm">{entry.employee_name}</p>
                  {entry.department && (
                    <p className="text-xs text-muted-foreground">{entry.department}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-bold text-lg">{entry.recognition_count}</p>
                  <p className="text-xs text-muted-foreground">{t("recognitionHub.leaderboard.kudos")}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${color}`} />
          <span className="text-xs text-muted-foreground">{label}</span>
        </div>
        <p className="text-2xl font-bold mt-1">{value ?? "—"}</p>
      </CardContent>
    </Card>
  );
}
